import type { TFunction } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { VIRTUOUS_ALREADY_CONNECTED_CODE } from '@feathr/blackbox';
import { toast } from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import { errorMessage } from '@feathr/hooks';
import { findRachisError } from '@feathr/rachis';

/** `meta.code` anhinga sends when Virtuous refuses the API key. */
const CREDENTIALS_REJECTED_CODE = 'credentials_rejected';
/** `meta.code` anhinga sends when magpie refused the request outright, so retrying won't help. */
const MAGPIE_REJECTED_CODE = 'magpie_rejected';

function hasErrorCode(error: unknown, code: string): boolean {
  return !!findRachisError(error, (entry) => entry.meta?.code === code);
}

/** The toast for a failed connect. */
export function getConnectFailure(error: unknown, t: TFunction): string {
  if (hasErrorCode(error, CREDENTIALS_REJECTED_CODE)) {
    return t('Virtuous didn’t accept that API key. Check it in Virtuous Connect and try again.');
  }
  if (hasErrorCode(error, VIRTUOUS_ALREADY_CONNECTED_CODE)) {
    return t('This Virtuous organization is already connected. Reload the page to see it.');
  }
  if (hasErrorCode(error, MAGPIE_REJECTED_CODE)) {
    return t("Couldn't connect to Virtuous. {{- error}}", { error: errorMessage(error, t) });
  }
  return t("Couldn't connect to Virtuous. Try again. {{- error}}", {
    error: errorMessage(error, t),
  });
}

export interface IVirtuousConnect {
  isModalOpen: boolean;
  isConnecting: boolean;
  openModal: () => void;
  closeModal: () => void;
  /** Connects with the API key. Toasts and resolves `false` on failure, so the modal stays open. */
  connect: (apiKey: string) => Promise<boolean>;
}

/** The API key connect flow behind the setup step. */
export function useVirtuousConnect(): IVirtuousConnect {
  const { t } = useTranslation();
  const { VirtuousIntegrations } = useStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  function openModal(): void {
    setIsModalOpen(true);
  }

  function closeModal(): void {
    if (!isConnecting) {
      setIsModalOpen(false);
    }
  }

  async function connect(apiKey: string): Promise<boolean> {
    setIsConnecting(true);
    try {
      const integration = await VirtuousIntegrations.findOrCreate();
      await integration.connect(apiKey.trim());
      setIsModalOpen(false);
      toast(t('Virtuous is connected.'), { type: 'success' });
      return true;
    } catch (error) {
      toast(getConnectFailure(error, t), { type: 'error' });
      return false;
    } finally {
      setIsConnecting(false);
    }
  }

  return { closeModal, connect, isConnecting, isModalOpen, openModal };
}
